"use client"

import { useState, useEffect } from "react"
import MapComponent from "./map" // Importación directa
import { Card, Button, Badge, Container, Row, Col } from "react-bootstrap" // Importar componentes de react-bootstrap
import { FaMapMarkerAlt, FaFilter, FaPlay, FaClock, FaMinus, FaExpand, FaTimes, FaStar, FaCar, FaRoute, FaSpinner, FaSearch } from "react-icons/fa" // Importar iconos de react-icons/fa
import authAxios from "../services/Token"

// Función para obtener el ID del usuario
const getUserId = () => {
  let ide = localStorage.getItem("idusuario")
  if (!ide) {
    ide = sessionStorage.getItem("idusuario")
  }
  return ide
}

// Función para convertir servicios de la API a formato de conductores
const convertServicesToDrivers = (services) => {
  if (!Array.isArray(services)) return []
  
  return services.map((service, index) => {
    // Determinar si está disponible basado en el campo real del API o el estado del servicio
    const isAvailable = typeof service.available === 'boolean'
      ? service.available
      : (service.estado_servicio === 2 || service.estado_servicio === 7 || service.estado_servicio === 8)
    
    // Generar timeline basado en el servicio
    const timeline = generateTimelineFromService(service)
    
    // Obtener la primera entrada del timeline para información de ubicación actual
    const currentLocation = timeline && timeline.length > 0 ? timeline[0] : null
    
    // Generar iniciales del nombre para el avatar
    const name = service.conductor_nombre || `Conductor ${index + 1}`
    const initials = name.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase()
    
    // Determinar categoría basada en el vehículo del timeline
    const vehicleId = currentLocation ? currentLocation.vehiculo : (service.vehiculo || 0)
    let category = ""
    if (vehicleId >= 400 && vehicleId < 500) category = "Premium"
    else if (vehicleId >= 300 && vehicleId < 400) category = "Estándar"
    else if (vehicleId >= 200 && vehicleId < 300) category = "Económico"
    else category = "General"
    if (!category) category = "General"
    
    // Generar rating basado en el ID del conductor (para consistencia)
    const rating = service.id ? (4.0 + (service.id % 10) * 0.1).toFixed(1) : "4.5"
    
    // Formatear hora disponible - usar la primera entrada del timeline
    const availableTime = currentLocation && currentLocation.time ? currentLocation.time : (timeline[0]?.time || "Disponible")
    
    // Usar las coordenadas del timeline si están disponibles
    const lat = currentLocation ? parseFloat(currentLocation.lat) : (parseFloat(service.latitud) || 4.6522)
    const lng = currentLocation ? parseFloat(currentLocation.lng) : (parseFloat(service.longitud) || -74.1059)
    
    // Obtener información del vehículo del timeline
    const vehicleInfo =  (service.vehicle || `Vehículo ${vehicleId}`)
    
    return {
      id: service.id || index + 1,
      name: name,
      category: category,
      available: isAvailable,
      lat: lat,
      lng: lng,
      phone: service.numero_contacto || `+52 55 ${1000 + index} ${2000 + index}`,
      vehicle: vehicleInfo,
      rating: rating,
      availableTime: availableTime,
      avatar: `/placeholder.svg?height=40&width=40&text=${initials}`,
      timeline: timeline,
      // Datos adicionales del servicio
      serviceData: service,
      // Información adicional organizada
      currentStatus: isAvailable ? "Disponible" : "En servicio",
      vehicleId: vehicleId,
      lastUpdate: currentLocation ? currentLocation.time : "N/A"
    }
  })
}

// Función para generar timeline basado en el servicio
const generateTimelineFromService = (service) => {
  const timeline = []
  
  // Si el servicio tiene datos de timeline reales, usarlos
  if (service.timeline && Array.isArray(service.timeline)) {
    return service.timeline.map((item, index) => ({
      id: item.id || index + 1,
      time: item.time || "08:00",
      action: item.action || "Actividad programada",
      lat: parseFloat(item.lat) || parseFloat(service.latitud) || 4.6522,
      lng: parseFloat(item.lng) || parseFloat(service.longitud) || -74.1059,
      status: item.status || "available",
      vehiculo: item.vehiculo || service.vehiculo,
      servicio: item.servicio || null
    }))
  }
  
  // Si no hay timeline real, generar uno basado en el servicio
  const baseTime = service.hora_inicial || "08:00"
  const isAvailable = service.estado_servicio === 2 || service.estado_servicio === 7 || service.estado_servicio === 8
  
  // Ubicación actual (primera entrada)
  timeline.push({
    id: 1,
    time: formatTime(baseTime),
    action: isAvailable ? "Ubicación actual - Disponible" : "En servicio",
    lat: parseFloat(service.latitud) || 4.6522,
    lng: parseFloat(service.longitud) || -74.1059,
    status: isAvailable ? "available" : "busy",
    vehiculo: service.vehiculo || 0,
    servicio: service.id || null
  })
  
  // Si hay dirección inicial, agregar punto de recogida
  if (service.direccion_inicial) {
    timeline.push({
    id: 2,
      time: addMinutesToTime(baseTime, 15),
      action: `Recogida en ${service.direccion_inicial}`,
      lat: parseFloat(service.latitud) || 4.6522,
      lng: parseFloat(service.longitud) || -74.1059,
      status: "busy",
      vehiculo: service.vehiculo || 0,
      servicio: service.id || null
    })
  }
  
  // Si hay dirección final, agregar punto de destino
  if (service.direccion_final) {
    timeline.push({
    id: 3,
      time: addMinutesToTime(baseTime, 45),
      action: `Destino: ${service.direccion_final}`,
      lat: parseFloat(service.latitud) || 4.6522,
      lng: parseFloat(service.longitud) || -74.1059,
      status: "busy",
      vehiculo: service.vehiculo || 0,
      servicio: service.id || null
    })
  }
  
  // Punto final - regreso a disponibilidad
  timeline.push({
    id: 4,
    time: addMinutesToTime(baseTime, 90),
    action: "Servicio completado - Disponible",
    lat: parseFloat(service.latitud) || 4.6522,
    lng: parseFloat(service.longitud) || -74.1059,
    status: "available",
    vehiculo: service.vehiculo || 0,
    servicio: service.id || null
  })
  
  return timeline
}



// Función para formatear tiempo
const formatTime = (timeString) => {
  if (!timeString) return "08:00"
  
  try {
    const time = new Date(`2000-01-01T${timeString}`)
    return time.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    })
  } catch {
    return timeString
  }
}

// Función para agregar minutos a una hora
const addMinutesToTime = (timeString, minutes) => {
  if (!timeString) return "08:00"
  
  try {
    const time = new Date(`2000-01-01T${timeString}`)
    time.setMinutes(time.getMinutes() + minutes)
    return time.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    })
  } catch {
    return timeString
  }
}

export default function ModernMapApp() {
  const [drivers, setDrivers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAvailableOnly, setShowAvailableOnly] = useState(false)
  const [showTimeline, setShowTimeline] = useState(false)
  const [timelineDriver, setTimelineDriver] = useState(null)
  const [animatingStep, setAnimatingStep] = useState(null)
  const [routeAnimation, setRouteAnimation] = useState(null)
  const [timelineMinimized, setTimelineMinimized] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Función para cargar datos de la API
  const fetchDriversData = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await authAxios.get(`exterior/servicios-mapa-general`)
      
      if (response.data && response.data.datos) {
        // Limpiar y validar los datos antes de procesarlos
        const cleanedData = response.data.datos.map(service => ({
          ...service,
          // Asegurar que los valores nulos se manejen correctamente
          conductor_nombre: service.name || `Conductor ${service.id || 'N/A'}`,
          numero_contacto: service.numero_contacto || '',
          latitud: service.latitud || '4.6522',
          longitud: service.longitud || '-74.1059',
          vehiculo: service.vehiculo || 0,
          // Si timeline existe, asegurar que tenga la estructura correcta
          timeline: service.timeline ? service.timeline.map(item => ({
            ...item,
            lat: item.lat || service.latitud || '4.6522',
            lng: item.lng || service.longitud || '-74.1059',
            time: item.time || '08:00',
            action: item.action || 'Actividad programada',
            status: item.status || 'available',
            vehiculo: item.vehiculo || service.vehiculo || 0,
            servicio: item.servicio || null
          })) : null
        }))
        
        const driversData = convertServicesToDrivers(cleanedData)
        setDrivers(driversData)
      } else {
        setDrivers([])
      }
    } catch (err) {
      console.error("Error al cargar datos de conductores:", err)
      setError("Error al cargar los datos. Por favor, intente nuevamente.")
      setDrivers([])
    } finally {
      setLoading(false)
    }
  }

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchDriversData()
  }, [])

  // Filtrar conductores por búsqueda y disponibilidad
  const filteredDrivers = drivers.filter((driver) => {
    const matchesSearch = searchTerm === "" || 
      driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.id.toString().includes(searchTerm) ||
      driver.vehicle.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesAvailability = !showAvailableOnly || driver.available
    
    return matchesSearch && matchesAvailability
  })

  // Obtener la primera ubicación para centrar el mapa
  const firstLocation = drivers.length > 0 ? {
    lat: parseFloat(drivers[0].lat),
    lng: parseFloat(drivers[0].lng)
  } : { lat: 4.6522, lng: -74.1059 }

  const handleDriverClick = (driver) => {
    setTimelineDriver(driver)
    setShowTimeline(true)
    setTimelineMinimized(false)
  }

  const closeTimeline = () => {
    setShowTimeline(false)
    setTimelineDriver(null)
    setAnimatingStep(null)
    setRouteAnimation(null)
  }

  const handleStepClick = (step, driver) => {
    setAnimatingStep(step)

    // Create route animation data
    const currentStepIndex = driver.timeline.findIndex((s) => s.id === step.id)
    const routeSteps = driver.timeline.slice(0, currentStepIndex + 1)

    setRouteAnimation({
      steps: routeSteps,
      currentStep: step,
      driver: driver,
    })
  }

  // Helper functions for colors
  const getCategoryColor = (category) => {
    switch (category) {
      case "Premium":
        return "linear-gradient(135deg, #10b981, #059669)" // Verde premium
      case "Estándar":
        return "linear-gradient(135deg, #34d399, #10b981)" // Verde estándar
      case "Económico":
        return "linear-gradient(135deg, #6ee7b7, #34d399)" // Verde claro
      default:
        return "linear-gradient(135deg, #d1fae5, #a7f3d0)" // Verde muy claro
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "available":
        return "#10b981" // Verde
      case "busy":
        return "#ef4444" // Rojo
      case "waiting":
        return "#f59e0b" // Amarillo
      case "break":
        return "#6b7280" // Gris
      default:
        return "#6b7280"
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case "available":
        return "Disponible"
      case "busy":
        return "Ocupado"
      case "waiting":
        return "Esperando"
      case "break":
        return "Descanso"
      default:
        return "Desconocido"
    }
  }

  const groupedDrivers = drivers.reduce((acc, driver) => {
    if (!acc[driver.category]) {
      acc[driver.category] = []
    }
    acc[driver.category].push(driver)
    return acc
  }, {})

    // Mostrar loading mientras se cargan los datos
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)" }}>
        <div className="text-center">
          <div className="mb-4">
            <FaSpinner className="text-success" style={{ fontSize: "3rem", animation: "spin 1s linear infinite" }} />
          </div>
          <h4 className="text-success mb-2">Cargando conductores...</h4>
          <p className="text-muted">Obteniendo datos de la API</p>
          <style>
            {`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    )
  }

  // Mostrar error si hay problemas
  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)" }}>
        <div className="text-center">
          <div className="mb-4">
            <div className="text-danger" style={{ fontSize: "3rem" }}>⚠️</div>
          </div>
          <h4 className="text-danger mb-2">Error al cargar datos</h4>
          <p className="text-muted mb-4">{error}</p>
          <Button 
            variant="success" 
            onClick={fetchDriversData}
            className="px-4 py-2"
          >
            <FaSpinner className="me-2" />
            Reintentar
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-vh-100" style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)" }}>
      <style>
        {`
          .overflow-auto::-webkit-scrollbar {
            width: 8px;
          }
          
          .overflow-auto::-webkit-scrollbar-track {
            background: #f0fdf4;
            border-radius: 4px;
          }
          
          .overflow-auto::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #10b981, #059669);
            border-radius: 4px;
          }
          
          .overflow-auto::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #059669, #047857);
          }
        `}
      </style>
      <Container fluid className="p-0 d-flex flex-column">
      <Row className="flex-grow-1 g-0 m-0">
        {/* Map Section - Left Side (75-80%) */}
          <Col xs={12} lg={showTimeline ? 8 : 9} className="position-relative p-0" style={{ height: '100vh' }}>
              <div className="position-absolute top-4 d-flex gap-3" style={{
                zIndex: 1000,
                left: showTimeline ? "2rem" : "calc(25% + 2rem)",
                transition: "left 0.5s ease-in-out"
              }}>
            <Button
              onClick={() => setShowAvailableOnly(!showAvailableOnly)}
                  variant="light"
                  className="shadow-lg border-0 px-4 py-2 fw-bold"
                  style={{ 
                    background: showAvailableOnly 
                      ? "linear-gradient(135deg, #10b981, #059669)" 
                      : "linear-gradient(135deg, #ffffff, #f8fafc)",
                    color: showAvailableOnly ? "white" : "#059669"
                  }}
            >
              <FaFilter className="me-2" />
              {showAvailableOnly ? "Mostrar Todos" : "Solo Disponibles"}
            </Button>
                <div
                  className="px-4 py-2 rounded-pill shadow-lg fw-bold text-white"
                  style={{ background: "linear-gradient(135deg, #34d399, #10b981)" }}
            >
              ✨ {filteredDrivers.length} conductores
                </div>
                <Button
                  onClick={fetchDriversData}
                  variant="light"
                  className="shadow-lg border-0 px-3 py-2 fw-bold"
                  style={{ 
                    background: "linear-gradient(135deg, #ffffff, #f8fafc)",
                    color: "#059669"
                  }}
                  disabled={loading}
                >
                  <FaSpinner className={`me-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
          </div>
            
          {/* Animation Status Indicator */}
          {animatingStep && (
                <div className="position-absolute top-4" style={{
                  zIndex: 1000,
                  right: showTimeline ? "calc(25% + 2rem)" : "2rem",
                  transition: "right 0.5s ease-in-out"
                }}>
                  <Card className="border-0 shadow-lg" style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                    <Card.Body className="d-flex align-items-center p-3 text-white">
                      <div className="me-3">
                    <div
                      className="bg-white rounded-circle"
                      style={{ width: "12px", height: "12px", animation: "pulse 1.5s infinite" }}
                    ></div>
                  </div>
                  <div>
                    <p className="mb-0 fw-bold small">🎯 Animando Ruta</p>
                    <p className="mb-0 small opacity-75">
                      {animatingStep.time} - {animatingStep.action}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
            
            <MapComponent 
              drivers={filteredDrivers} 
              onDriverClick={handleDriverClick} 
              routeAnimation={routeAnimation}
              initialLocation={firstLocation}
            />
        </Col>
          
        {/* Drivers Sidebar - Right Side (20-25%) */}
        <Col
          xs={12}
          lg={showTimeline ? 4 : 3}
            className="p-0 h-100 shadow-lg overflow-auto d-flex flex-column"
            style={{ background: "linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)" }}
        >
            {/* Header */}
            <div 
              className="p-4 border-bottom"
              style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
            >
              <h2 className="h4 mb-2 d-flex align-items-center text-white">
                <FaCar className="me-2" />
                Conductores Activos
              </h2>
              <p className="text-white opacity-75 mb-0">
              {drivers.filter((d) => d.available).length} disponibles de {drivers.length}
            </p>
          </div>
            
            {/* Search Input */}
            <div className="p-3 border-bottom" style={{ background: "linear-gradient(135deg, #f0fdf4, #dcfce7)" }}>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control border-0 shadow-sm"
                  placeholder="Buscar conductor por nombre, ID o vehículo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "12px 16px 12px 45px",
                    fontSize: "14px",
                    border: "1px solid #e5e7eb"
                  }}
                />
                <div className="position-absolute top-50 start-0 translate-middle-y ms-3">
                  <FaSearch className="text-muted" style={{ fontSize: "16px" }} />
                </div>
                {searchTerm && (
                  <button
                    className="position-absolute top-50 end-0 translate-middle-y me-3 btn btn-link p-0"
                    onClick={() => setSearchTerm("")}
                    style={{ color: "#6b7280", textDecoration: "none" }}
                  >
                    <FaTimes style={{ fontSize: "14px" }} />
                  </button>
                )}
              </div>
              {searchTerm && (
                <div className="mt-2">
                  <small className="text-success fw-medium">
                    {filteredDrivers.length} resultado{filteredDrivers.length !== 1 ? 's' : ''} encontrado{filteredDrivers.length !== 1 ? 's' : ''}
                  </small>
                </div>
              )}
            </div>
            
            <div 
              className="flex-grow-1 overflow-auto" 
              style={{ 
                height: "calc(100vh - 200px)",
                scrollbarWidth: "thin",
                scrollbarColor: "#10b981 #f0fdf4"
              }}
            >
              {filteredDrivers.length === 0 ? (
                <div className="p-4 text-center">
                  <div className="text-muted mb-3">
                    <FaSearch style={{ fontSize: "3rem", opacity: 0.5 }} />
                  </div>
                  <h6 className="text-muted">No se encontraron conductores</h6>
                  <small className="text-muted">
                    {searchTerm ? `No hay resultados para "${searchTerm}"` : "No hay conductores disponibles"}
                  </small>
                </div>
              ) : (
                <div className="p-3">
                  {Object.entries(
                    filteredDrivers.reduce((acc, driver) => {
                      if (!acc[driver.category]) {
                        acc[driver.category] = []
                      }
                      acc[driver.category].push(driver)
                      return acc
                    }, {})
                  ).map(([category, categoryDrivers]) => (
              <div key={category} className="mb-4">
                      <h3 className="fw-bold text-dark mb-3 d-flex align-items-center h5">
                  <span
                          className="rounded-circle me-2 shadow-sm"
                    style={{
                      width: "1rem",
                      height: "1rem",
                            background: getCategoryColor(category),
                    }}
                  ></span>
                        {category} ({categoryDrivers.length})
                </h3>

                <div className="d-flex flex-column gap-3">
                  {categoryDrivers.map((driver) => {
                    // Debug: log driver info y condición de disponibilidad
                    console.log('DRIVER DEBUG', driver, driver.timeline, 'isAvailable:', driver.available && driver.timeline.every(step => step.status === 'available'));
                    return (
                    <Card
                      key={driver.id}
                        className={`cursor-pointer transition-all duration-300 hover-shadow-lg border-0 ${
                        driver.available
                            ? "shadow-sm"
                            : "opacity-75 shadow-sm"
                        } ${timelineDriver?.id === driver.id ? "shadow-lg scale-105" : ""}`}
                        style={{ 
                          background: driver.available 
                            ? "linear-gradient(135deg, #ffffff, #f0fdf4)" 
                            : "linear-gradient(135deg, #f8fafc, #f1f5f9)",
                          border: timelineDriver?.id === driver.id ? "2px solid #10b981" : "1px solid #e5e7eb"
                        }}
                      onClick={() => handleDriverClick(driver)}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="position-relative me-3">
                            <div
                                  className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                              style={{
                                width: "48px",
                                height: "48px",
                                    background: getCategoryColor(driver.category),
                              }}
                            >
                              {driver.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </div>
                            <div
                                  className="position-absolute bottom-0 end-0 rounded-circle border border-2 border-white shadow-sm"
                                  style={{ 
                                    width: "12px", 
                                    height: "12px",
                                    backgroundColor: getStatusColor(driver.available ? "available" : "busy")
                                  }}
                            ></div>
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                                  <h6 className="mb-0 fw-bold text-dark">{driver.name}</h6>
                              <Badge
                                    className="text-uppercase fw-bold px-2 py-1"
                                    style={{ 
                                      background: driver.available && driver.timeline.every(step => step.status === 'available')
                                        ? "linear-gradient(135deg, #10b981, #059669)" 
                                        : "linear-gradient(135deg, #ef4444, #dc2626)",
                                      color: "white"
                                    }}
                              >
                                  {driver.available && driver.timeline.every(step => step.status === 'available') ? "Disponible" : "Ocupado"}
                              </Badge>
                            </div>
                               
                                <small className="text-muted mt-1 fw-medium">{driver.vehicle}</small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                    )
                  })}
                </div>
              </div>
            ))}
                </div>
              )}
          </div>
        </Col>
      </Row>
        
      {/* Timeline Sliding Panel */}
      {showTimeline && timelineDriver && (
        <div
            className={`position-fixed end-0 transition-width ${timelineMinimized ? "w-16" : "col-lg-4"}`}
            style={{ 
              transition: "all 0.5s ease-in-out",
              top: "4.5rem",
              height: "calc(100vh - 3rem)",
              marginRight: "1rem",
              maxWidth: "400px",
              zIndex: 1000,
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(255, 255, 255, 0.95)"
            }}
        >
            <Card className="h-100 border-0 rounded-4 shadow-2xl" style={{ 
              background: "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 253, 244, 0.95) 100%)",
              border: "1px solid rgba(16, 185, 129, 0.2)",
              backdropFilter: "blur(15px)"
            }}>
            {/* Header */}
              <Card.Header 
                className="p-4 d-flex align-items-center justify-content-between border-0"
                style={{ 
                  background: "linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9))",
                  backdropFilter: "blur(10px)"
                }}
              >
              {!timelineMinimized && (
                <div className="d-flex align-items-center">
                  <div
                      className="rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold shadow-sm"
                      style={{ 
                        width: "48px", 
                        height: "48px", 
                        background: "linear-gradient(135deg, #ffffff, #f0fdf4)",
                        color: "#059669"
                      }}
                  >
                    {timelineDriver.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div>
                      <h5 className="mb-0 fw-bold text-white">{timelineDriver.name}</h5>
                      <Badge 
                        className="text-uppercase fw-bold px-2 py-1"
                        style={{ 
                          background: "linear-gradient(135deg, #34d399, #10b981)",
                          color: "white"
                        }}
                      >
                      {timelineDriver.category}
                    </Badge>
                  </div>
                </div>
              )}

              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setTimelineMinimized(!timelineMinimized)}
                  className="text-white p-0 rounded-circle"
                  style={{ width: "32px", height: "32px" }}
                >
                  {timelineMinimized ? <FaExpand className="fs-6" /> : <FaMinus className="fs-6" />}
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={closeTimeline}
                  className="text-white p-0 rounded-circle"
                  style={{ width: "32px", height: "32px" }}
                >
                  <FaTimes className="fs-6" />
                </Button>
              </div>
            </Card.Header>

            {/* Timeline Content */}
            {!timelineMinimized && (
              <Card.Body className="p-4 h-100 d-flex flex-column">
                <div className="mb-4">
                    <h4 className="fw-bold text-dark mb-2 d-flex align-items-center">
                      <FaClock className="me-2" style={{ color: "#10b981" }} />
                    Timeline del día
                  </h4>
                    <p className="text-muted small">Haz clic en cualquier paso para ver la ruta animada</p>
                </div>

                <div className="flex-grow-1 overflow-auto pe-2">
                  {timelineDriver.timeline.map((step, index) => (
                    <div
                      key={step.id}
                        className={`position-relative d-flex align-items-start p-3 mb-3 rounded-3 cursor-pointer transition-all hover-shadow-lg ${
                        animatingStep?.id === step.id
                            ? "shadow-lg scale-105"
                            : "shadow-sm"
                        }`}
                        style={{ 
                          background: animatingStep?.id === step.id 
                            ? "linear-gradient(135deg, #d1fae5, #a7f3d0)" 
                            : "linear-gradient(135deg, #ffffff, #f8fafc)",
                          border: animatingStep?.id === step.id 
                            ? "2px solid #10b981" 
                            : "1px solid #e5e7eb"
                        }}
                      onClick={() => handleStepClick(step, timelineDriver)}
                    >
                      {/* Timeline line */}
                      {index < timelineDriver.timeline.length - 1 && (
                        <div
                          className="position-absolute"
                          style={{
                            left: "23px",
                            top: "40px",
                            width: "2px",
                            height: "40px",
                              background: "linear-gradient(to bottom, #10b981, #34d399)",
                          }}
                        ></div>
                      )}

                      {/* Status indicator */}
                      <div className="flex-shrink-0 position-relative">
                        <div
                            className="rounded-circle shadow-sm"
                          style={{
                            width: "24px",
                            height: "24px",
                            minWidth: "24px",
                              backgroundColor: getStatusColor(step.status),
                              border: "2px solid #ffffff",
                          }}
                        ></div>
                        {animatingStep?.id === step.id && (
                          <div
                              className="position-absolute top-0 start-0 rounded-circle animate-pulse"
                              style={{ 
                                width: "24px", 
                                height: "24px", 
                                animation: "pulse 1s infinite",
                                background: "linear-gradient(135deg, #10b981, #34d399)"
                              }}
                          ></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="ms-3 flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                            <span className="fs-5 fw-bold text-dark">{step.time}</span>
                          <Badge
                              className="fw-bold px-2 py-1"
                              style={{ 
                                background: "linear-gradient(135deg, #f3f4f6, #e5e7eb)",
                                color: "#374151"
                              }}
                          >
                            {getStatusText(step.status)}
                          </Badge>
                        </div>
                          <p className="text-dark fw-bold mb-2 small">{step.action}</p>
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center text-muted small">
                            <FaMapMarkerAlt className="me-1" />
                            <span className="font-monospace">
                              {step.lat.toFixed(4)}, {step.lng.toFixed(4)}
                            </span>
                          </div>
                          {animatingStep?.id === step.id && (
                              <div className="d-flex align-items-center small" style={{ color: "#10b981" }}>
                              <FaPlay className="me-1" />
                                <span className="fw-bold">Animando...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                  <div className="mt-4 pt-4 border-top border-light">
                    <div className="d-flex align-items-center justify-content-between small text-muted p-3 rounded-3" style={{ background: "linear-gradient(135deg, #f0fdf4, #dcfce7)" }}>
                      <span className="fw-bold text-dark">📍 Total: {timelineDriver.timeline.length} paradas</span>
                      <span className="fw-bold text-dark">
                      🕐 Último: {timelineDriver.timeline[timelineDriver.timeline.length - 1].time}
                    </span>
                  </div>
                </div>
              </Card.Body>
            )}

            {/* Minimized state */}
            {timelineMinimized && (
              <div className="p-2 d-flex flex-column align-items-center gap-2">
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                    style={{ 
                      width: "32px", 
                      height: "32px", 
                      background: "linear-gradient(135deg, #10b981, #059669)"
                    }}
                >
                  {timelineDriver.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                  <div className="text-center">
                    <div className="fw-bold small text-dark">{timelineDriver.timeline.length}</div>
                    <div className="text-muted small">paradas</div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </Container>
    </div>
  )
}
